<script>
import { defineComponent } from 'vue'
import { usePlatform } from '@monogrid/vue3-boost/functions'

const sleep = (time = 50) => new Promise(resolve => setTimeout(resolve, time))

export default defineComponent({
  name: 'ScrubbleVideo',
  components: {
  },
  props: {
    src: { type: String, required: true },
    framePerSecond: { type: Number, default: 24 },
    loadingStrategy: { type: Array, default: () => ([48, 24, 8, 4, 2]) },
    start: { type: Number, default: 0 },
    end: { type: Number, default: -1 }
  },
  emits: ['init'],
  setup () {
    const platform = usePlatform()
    return {
      platform,
      android: platform.android
    }
  },
  data () {
    return {
      width: 0,
      height: 0,
      duration: 0,
      frames: {},
      currentIndex: '0',
      interval: 0,
      currentStrategy: 0
    }
  },
  mounted () {
    this.tlData = { currentTime: 0 }
    this.baseInterval = parseFloat((1 / this.framePerSecond).toFixed(2))
    this.interval = this.baseInterval * this.loadingStrategy[this.currentStrategy]
  },
  methods: {
    addAnimation (timeline, time) {
      timeline.fromTo(this.tlData,
        { currentTime: 0 },
        {
          currentTime: this.$refs.video.duration || 1,
          duration: this.$refs.video.duration,
          ease: 'none',
          onUpdate: () => this.onProgress()
        }, 0)
      timeline.addLabel('end', timeline.duration())
    },

    async onLoadedMetadata (e) {
      e.target.removeAttribute('autoplay')
      e.target.pause()
      if (this.android) {
        await this.initGeneration()
      }
      this.$emit('init')
    },

    convertProgressToTime (progress) {
      let tmp = this.duration * progress
      tmp = this.getNearestIndex(tmp)
      return tmp
    },

    getNearestIndex (time) {
      const gran = this.interval
      let tmp = Math.floor(time / gran) * gran
      tmp = parseFloat(tmp.toFixed(2))
      return tmp
    },

    onProgress () {
      if (this.android) {
        const tmp = this.getNearestIndex(this.tlData.currentTime)
        this.lastTriedIndex = tmp
        if (typeof (this.frames[tmp]) !== 'undefined' && this.frames[tmp].status === 'ready') {
          this.currentIndex = tmp
        }
      } else {
        this.$refs.video.currentTime = this.tlData.currentTime
      }
    },

    async initGeneration () {
      const video = this.$refs.video
      // workaround chromium metadata bug (https://stackoverflow.com/q/38062864/993683)
      while ((video.duration === Infinity || isNaN(video.duration)) && video.readyState < 2) {
        await sleep(200)
        video.currentTime = 10000000 * Math.random()
      }

      video.addEventListener('seeked', () => {
        if (this.seekResolve) {
          this.seekResolve()
          this.seekResolve = undefined
        }
      })

      this.width = video.videoWidth
      this.height = video.videoHeight
      this.duration = video.duration
      let end = this.duration
      if (this.end !== -1 && this.end < this.duration) {
        end = this.end
        this.duration = end - this.start
      } else {
        this.duration -= this.start
      }
      this.endTime = end
      await this.generateFrames()
    },

    async generateFrames () {
      const video = this.$refs.video
      const orderedFrames = []
      let currentTime = this.start
      let currentProgress = 0
      // create frames
      while (currentTime < this.endTime) {
        currentTime = parseFloat(currentTime.toFixed(2))
        currentProgress = parseFloat(currentProgress.toFixed(2))
        const k = currentProgress.toString()
        if (typeof (this.frames[k]) === 'undefined') {
          this.frames[k] = {
            status: 'unready',
            time: currentTime
          }
          orderedFrames.push(k)
        }
        currentTime += this.interval
        currentProgress += this.interval
      }

      currentTime = 0
      for (let i = 0; i < orderedFrames.length; i++) {
        const index = orderedFrames[i]
        if (this.frames[index].status !== 'unready') return
        this.frames[index].status = 'locked'
        const timeWanted = this.frames[index].time
        await new Promise((resolve, reject) => {
          video.currentTime = timeWanted
          this.seekResolve = resolve
        })

        const canvas = this.$refs[`frame_${index}`][0]
        if (typeof (canvas) !== 'undefined' && typeof (canvas.getContext) !== 'undefined') {
          await sleep(100)
          const context = canvas.getContext('2d')
          context.drawImage(video, 0, 0, this.width, this.height)
          this.frames[index].status = 'ready'
        }
      }
      if (this.loadingStrategy[this.currentStrategy + 1]) {
        this.currentStrategy++
        this.interval = this.baseInterval * this.loadingStrategy[this.currentStrategy]
        this.generateFrames()
      }
    }
  }
})
</script>

<template>
  <div class="vue-scrubbable-video">
    <video
      ref="video"
      muted
      playsinline
      autoplay
      preload="auto"
      :class="{'video-hidden': android}"
      @loadedmetadata="onLoadedMetadata"
    >
      <source :src="src" type="video/mp4" />
    </video>
    <canvas
      v-for="(frame, index) in frames"
      v-show="currentIndex == index"
      :key="`frame_${index}`"
      :ref="`frame_${index}`"
      :data-frame="index"
      :width="width"
      :height="height"
    ></canvas>
  </div>
</template>

<style lang="scss" scoped>
video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-hidden {
  opacity: 0;
}
</style>
